/**
 * Stage-Finance-only payment timing helpers.
 * Controls WHEN P&L cost atoms are paid — never invents amounts.
 */

import { allocateExact, clamp, clampInt, n } from './math.js';

export const DEFAULT_CONSTRUCTION_PAYMENT_TRANCHES = [0.1, 0.25, 0.3, 0.25, 0.1];

/** Normalize an array of tranche shares so they are non-negative and sum to 1. */
export function normalizeConstructionPaymentTranches(raw) {
  const defaults = DEFAULT_CONSTRUCTION_PAYMENT_TRANCHES;
  const src = Array.isArray(raw) && raw.length === defaults.length ? raw : defaults;
  const vals = src.map((v) => Math.max(0, n(v)));
  const sum = vals.reduce((s, v) => s + v, 0);
  if (sum <= 0) return [...defaults];
  return vals.map((v) => v / sum);
}

/**
 * Edit one tranche share and rebalance the others so the set always totals 100%.
 * Same UX contract as linked sales-allocation sliders.
 */
export function linkConstructionPaymentTranches(current, index, nextShare) {
  const defaults = DEFAULT_CONSTRUCTION_PAYMENT_TRANCHES;
  const cur = normalizeConstructionPaymentTranches(current);
  if (index < 0 || index >= defaults.length) return cur;
  const clamped = Math.min(1, Math.max(0, Number(nextShare) || 0));
  const others = cur.map((v, i) => (i === index ? 0 : v));
  const otherSum = others.reduce((s, v) => s + v, 0);
  const remaining = Math.max(0, 1 - clamped);
  const next = cur.map(() => 0);
  next[index] = clamped;
  if (remaining <= 0) {
    return normalizeConstructionPaymentTranches(next);
  }
  if (otherSum <= 1e-12) {
    const each = remaining / (defaults.length - 1);
    for (let i = 0; i < defaults.length; i += 1) {
      if (i !== index) next[i] = each;
    }
  } else {
    for (let i = 0; i < defaults.length; i += 1) {
      if (i !== index) next[i] = remaining * (others[i] / otherSum);
    }
  }
  return normalizeConstructionPaymentTranches(next);
}

/**
 * Place tranche weights on months spanning [startMonth, endMonth] inclusive.
 * Duplicate month hits accumulate; allocateExact renormalizes to the amount.
 */
export function constructionTrancheWeights(totalMonths, startMonth, endMonth, tranches) {
  const weights = Array(Math.max(0, totalMonths)).fill(0);
  if (totalMonths <= 0) return weights;
  const shares = normalizeConstructionPaymentTranches(tranches);
  const start = Math.max(1, Math.min(totalMonths, startMonth));
  const end = Math.max(start, Math.min(totalMonths, endMonth));
  const span = end - start + 1;
  const count = shares.length;
  for (let i = 0; i < count; i += 1) {
    const offset = count === 1 || span === 1
      ? 0
      : Math.round((i * (span - 1)) / (count - 1));
    const month = Math.min(end, start + offset);
    weights[month - 1] += shares[i];
  }
  return weights;
}

export function allocateByTranches(totalMonths, startMonth, endMonth, amount, tranches) {
  return allocateExact(
    amount,
    constructionTrancheWeights(totalMonths, startMonth, endMonth, tranches),
  );
}

export function hydratePaymentTiming(raw = {}) {
  const a = raw && typeof raw === 'object' ? raw : {};
  return {
    landDepositPct: clamp(n(a.landDepositPct ?? 0), 0, 1),
    /** Signing month for the land deposit (1-indexed). Default M1. */
    landSigningMonth: clampInt(a.landSigningMonth, 1, 240, 1),
    constructionStartOffsetFromAuth: clampInt(
      a.constructionStartOffsetFromAuth ?? 2,
      0,
      60,
      2,
    ),
    constructionPaymentTranches: normalizeConstructionPaymentTranches(
      a.constructionPaymentTranches,
    ),
  };
}
